import React, { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { IoIosClose, IoIosImage } from 'react-icons/io'
import ReactCrop, { Crop } from 'react-image-crop'
import 'react-image-crop/lib/ReactCrop.scss'
import './ImageInput.scss'
import { getCroppedImage } from './utils'

const ImageInput = ({
  label,
  onFilesChange,
  acceptedFileTypes = `image/*`,
  multiple = false,
  aspectRatio = 1,
  defaultCrop = {
    unit: `%`,
    width: 100,
    y: 0,
  } as Crop,
}) => {
  const [file, setFile] = useState(null)
  const [crop, setCrop] = useState({
    ...defaultCrop,
    aspect: aspectRatio,
  } as Crop)
  const [preview, setPreview] = useState(``)
  const imageReference = useRef(null as HTMLImageElement)

  const onDrop = useCallback((f) => {
    if(f.length === 1){
      setPreview(URL.createObjectURL(f[0]))
      setFile(f[0])
      onFilesChange(f)
    }
  }, [onFilesChange])

  const onDelete = useCallback((event) => {
    onFilesChange([])
    setFile(null)
    setCrop(defaultCrop)
    event.stopPropagation()
  }, [defaultCrop, onFilesChange])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptedFileTypes,
    multiple,
    onDrop,
  })

  const onChangeCrop = useCallback((c: Crop) => {
    setCrop(c)
  }, [])

  const onCropComplete = useCallback(async (c: Crop) => {
    const croppedImage = await getCroppedImage(imageReference.current, c)
    onFilesChange([croppedImage])
  }, [onFilesChange])

  const onClickPreview = useCallback((event) => event.stopPropagation(), [])
  const onImageLoad = useCallback((image: HTMLImageElement) => {
    imageReference.current = image
    const { width, height } = image

    const percentWidth = height > width ? 100 : ((height * aspectRatio) / width) * 100
    const percentHeight = width > height ? 100 : (width / aspectRatio / height) * 100

    setCrop({
      aspect: aspectRatio,
      unit: `%`,
      width: percentWidth,
      x: (100 - percentWidth) / 2,
      y: (100 - percentHeight) / 2,
    })
  
    return false
  }, [aspectRatio])

  return (
    <div className="image-input-container" {...getRootProps()}>
      <label>
        <strong>{label}</strong>
      </label>
      <input {...getInputProps()} />
      {
        file !== null
          ? (
            <div className="previews">
              <div className="preview" key={preview}>
                <div className="delete" onClick={onDelete}>
                  <IoIosClose size="1.5rem" />
                </div>
                <div onClick={onClickPreview}>
                  <ReactCrop
                    src={preview}
                    crop={crop}
                    onChange={onChangeCrop}
                    onImageLoaded={onImageLoad}
                    onComplete={onCropComplete}
                    keepSelection
                  />
                </div>
              </div>
            </div>
          )
          : (
            <div className="drop-zone">
              <IoIosImage size="2rem" />
              {
                isDragActive
                  ? <p>Drop your image here</p>
                  : <p>Drag 'n' drop an image here, or click to select one</p>
              }
            </div>
          )
        }
    </div>
  )
}

export default ImageInput